import { useState } from "react";
import { yerelGunBasi } from "@koclukpanel/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  FiltreSeridi,
  Iskelet,
  Kart,
  Rozet,
  Secim,
  Sekmeler,
  Tablo,
  cn,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  abonelikAc,
  abonelikGetir,
  paketTanimla,
  paketleriGetir,
  tahsilatIsle,
  listeKesintiMetni,
  tahsilatKuyrugu,
  type AbonelikGorunumu,
  type AbonelikTaksiti,
  type OdemeYontemi,
  type Periyot,
} from "./api";

/**
 * FINANS — abonelik ve tahsilat (F-BIL)
 *
 * ONLINE ODEME VE FATURA BU EKRANDA YOK: ikisi de saglayici secimi
 * bekliyor (§9.18, §9.2). Ekranda "yakinda" diye bos bir dugme de
 * yok — olmayan bir ozelligi varmis gibi gostermek, hic gostermemekten
 * kotu.
 */

const PERIYOT_ADI: Record<Periyot, string> = {
  MONTHLY: "Aylık",
  QUARTERLY: "3 aylık",
  SEMIANNUAL: "6 aylık",
  ANNUAL: "Yıllık",
  CUSTOM: "Özel",
};

const YONTEM_ADI: Record<OdemeYontemi, string> = {
  ONLINE: "Online ödeme",
  BANK_TRANSFER: "Havale / EFT",
  CASH: "Nakit",
  OTHER: "Diğer",
};

/**
 * Elle tahsilat edilebilen yontemler.
 *
 * `ONLINE` BILEREK YOK: online tahsilat saglayicidan gelir, elle
 * isaretlenmez. Secilebilseydi finans sorumlusu gelmemis bir online
 * odemeyi "geldi" diye isaretleyebilir ve mutabakat tutmazdi.
 */
const ELLE_YONTEMLER: OdemeYontemi[] = ["BANK_TRANSFER", "CASH", "OTHER"];

function tarih(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Sunucu parayi dizgi gonderiyor; ekran da dizgi gosteriyor. */
function para(deger: string): string {
  const sayi = Number(deger);
  if (!Number.isFinite(sayi)) return deger;
  return (
    sayi.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺"
  );
}

export function Finans() {
  const [gorunum, setGorunum] = useState<"kuyruk" | "paketler">("kuyruk");

  return (
    <div className="flex flex-col gap-4">
      {/*
        DORDUNCU elle yazilmis `role="tab"` seridi kaldirildi (`Roller`,
        `Loglar`, `Personel` ve burasi). Hepsi ayni yarim desendi:
        `aria-selected` var, ok tuslariyla gezinme / gezinen odak /
        `tabpanel` bagi YOK. Ekran okuyucu kullanicisina tutulmayan bir
        soz vermektense `Sekmeler` gercekte olani soyluyor.
      */}
      <Sekmeler
        etiket="Finans görünümü"
        secili={gorunum}
        onSec={setGorunum}
        sekmeler={[
          { deger: "kuyruk", etiket: "Tahsilat kuyruğu" },
          { deger: "paketler", etiket: "Paketler" },
        ]}
      />

      {gorunum === "kuyruk" ? <TahsilatKuyrugu /> : <Paketler />}
    </div>
  );
}

// ---------------------------------------------------------------
// TAHSILAT KUYRUGU
// ---------------------------------------------------------------

function TahsilatKuyrugu() {
  const istemci = useQueryClient();
  const [gun, setGun] = useState(14);
  /*
    ================================================================
    ARAMA — 92 SATIRDA TEK TEK BAKMAK YOK (07.09.2026)
    ================================================================
    Kuyrukta tek denetim "kaç gün ileriye bakılsın"dı; belirli bir
    öğrencinin ödemesini bulmanın yolu doksan iki satırı gözle
    taramaktı. Paneldeki her liste ekranında "Ara" var — bu ekranda
    olmaması bir eksiklik değil TUTARSIZLIKTI.

    Süzgeç İSTEMCİDE: liste zaten `gun` süzgeciyle sunucudan gelmiş,
    en fazla `LISTE_SINIRI` satır. Sunucuya ikinci bir sorgu atmak
    yazarken her harfte ağ isteği demek olurdu.
  */
  const [ara, setAra] = useState("");
  const [acik, setAcik] = useState<string | null>(null);
  const [yontem, setYontem] = useState<OdemeYontemi>("BANK_TRANSFER");
  const [not, setNot] = useState("");

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["tahsilat-kuyrugu", gun],
    queryFn: () => tahsilatKuyrugu(gun),
  });

  const mut = useMutation({
    mutationFn: (taksitId: string) => tahsilatIsle(taksitId, { yontem, not: not.trim() || null }),
    onSuccess: async () => {
      setAcik(null);
      setNot("");
      await istemci.invalidateQueries({ queryKey: ["tahsilat-kuyrugu"] });
      await istemci.invalidateQueries({ queryKey: ["abonelik"] });
    },
  });

  /*
    Aramada ad, numara ve paket birlikte taraniyor: finans sorumlusu
    kimi "Ahmet" diye, kimi "177" diye ariyor.
  */
  const sorgu = ara.trim().toLocaleLowerCase("tr");
  const suzulmus = sorgu
    ? (data?.taksitler ?? []).filter(
        (t) =>
          t.ogrenciAdi.toLocaleLowerCase("tr").includes(sorgu) ||
          String(t.ogrenciNo).includes(sorgu) ||
          t.paketAdi.toLocaleLowerCase("tr").includes(sorgu),
      )
    : (data?.taksitler ?? []);

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Tahsilat kuyruğu alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        HAM `<select>` KALDIRILDI. Alanin alti gorsel hali
        (`alan-bicimi.ts`) tek yerde tanimli ve elle yazilan her alan o
        sozlesmenin disinda kaliyor: odak halkasi, hata hali ve salt
        okunur hâli burada farkli ciziliyordu.
      */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56">
          <Secim
            etiket="Kaç gün ileriye bakılsın"
            value={gun}
            onChange={(e) => setGun(Number(e.target.value))}
          >
            {[7, 14, 30, 60, 90].map((g) => (
              <option key={g} value={g}>
                {g} gün
              </option>
            ))}
          </Secim>
        </div>
        <div className="min-w-56 flex-1">
          <Alan
            etiket="Ara"
            type="search"
            value={ara}
            onChange={(e) => setAra(e.target.value)}
            yardim="Öğrenci adı, öğrenci numarası ya da paket adı."
          />
        </div>
      </div>

      {/*
        LISTE KESILDIYSE SOYLENIYOR — G-UYG-11. Tahsilat kuyrugu tam da
        kesilmenin pahaliya patlayacagi yer: finans sorumlusu "bu kadar
        gecikmis tahsilat var" diye bakip kalani hic gormezse, gormedigi
        taksitler tahsil edilmeden kalir.
      */}
      <FiltreSeridi
        filtreler={
          ara.trim()
            ? [{ anahtar: "ara", etiket: `“${ara.trim()}” aramasında`, onKaldir: () => setAra("") }]
            : []
        }
        onTemizle={() => setAra("")}
        bilgi={listeKesintiMetni(data, "taksit")}
      />

      {data.taksitler.length === 0 ? (
        <BosDurum
          ikon="tamam"
          baslik="Bu aralıkta bekleyen tahsilat yok"
          aciklama={`Önümüzdeki ${gun} günde vadesi gelen ödenmemiş taksit bulunmuyor.`}
        />
      ) : suzulmus.length === 0 ? (
        /*
          BOS DURUM SUZGECE GORE KONUSUYOR: "tahsilat yok" ile "aramaya
          uyan yok" ayri seyler ve ikincisinde yapilacak sey aramayi
          degistirmek.
        */
        <BosDurum
          baslik="Aramaya uyan taksit yok"
          aciklama={`“${ara.trim()}” için sonuç bulunamadı. Aramayı değiştirin ya da temizleyin.`}
          eylem={
            <Buton gorunum="ikincil" onClick={() => setAra("")}>
              Aramayı temizle
            </Buton>
          }
        />
      ) : (
        /**
         * ================================================================
         * KART DEĞİL TABLO — ÖLÇÜLDÜ
         * ================================================================
         * Kart satırı 78px yer kaplıyordu ve 1440×900'de 20 taksitin
         * yalnızca 8'i görünüyordu. Tahsilat kuyruğu TARAMA ekranı:
         * finans sorumlusu hangi tutarın hangi vadede geciktiğine
         * bakıyor ve rakamların alt alta hizalanması gerekiyor —
         * kartta her tutar farklı yerde başlıyordu.
         */
        <Tablo
          satirlar={suzulmus}
          anahtar={(t) => t.id}
          kartBasligi={0}
          sutunlar={[
            {
              /*
                ADI ÜSTTE, PAKET ALTTA. Önce yalnızca paket adı
                çiziliyordu ve sütun başlığı "Öğrenci / paket" olduğu
                hâlde ekranda hiçbir öğrenci adı yoktu.
              */
              baslik: "Öğrenci / paket",
              mobil: true,
              hucre: (t) => (
                <span className="flex flex-col">
                  <span className="text-notr-900 font-medium">{t.ogrenciAdi}</span>
                  <span className="text-notr-600 text-xs">
                    No {t.ogrenciNo} · {t.paketAdi}
                  </span>
                </span>
              ),
            },
            { baslik: "Taksit", sayisal: true, hucre: (t) => `${t.sira}.` },
            // Tutar SAYISAL: rakamlar sağa yaslı ve `tabular-nums` ile
            // hizalı olmadan karşılaştırılamıyor.
            { baslik: "Tutar", mobil: true, sayisal: true, hucre: (t) => para(t.tutar) },
            { baslik: "Vade", mobil: true, hucre: (t) => tarih(t.vade) },
            {
              baslik: "Durum",
              mobil: true,
              // Gecikme SUNUCUDA anlık hesaplanıyor; ekran onu okuyor.
              hucre: (t) => (t.gecikmisMi ? <Rozet ton="hata">Gecikmiş</Rozet> : null),
            },
            {
              baslik: "İşlem",
              // Telefonda da gorunur: isaretlenmeseydi `Tablo` bu
              // sutunu dar ekranda gizler ve satirin TEK islemi
              // telefondan erisilemez olurdu (07.08.2026, 375px).
              mobil: true,
              hucre: (t) => (
                <div className="flex justify-end">
                  <Buton
                    gorunum="sessiz"
                    onClick={() => setAcik(acik === t.id ? null : t.id)}
                    aria-expanded={acik === t.id}
                  >
                    {acik === t.id ? "Kapat" : "Tahsilat işle"}
                  </Buton>
                </div>
              ),
            },
          ]}
          altSatir={(t) =>
            acik === t.id ? (
              <form
                className="border-notr-100 bg-notr-50 flex flex-col gap-3 border-b px-3 py-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  mut.mutate(t.id);
                }}
              >
                <Secim
                  etiket="Ödeme yöntemi"
                  value={yontem}
                  onChange={(e) => setYontem(e.target.value as OdemeYontemi)}
                >
                  {/*
                    `ONLINE` listede YOK: online tahsilat sağlayıcıdan
                    gelir, elle işaretlenmez. Seçilebilseydi gelmemiş
                    bir ödeme "geldi" diye işaretlenir ve mutabakat
                    tutmazdı.
                  */}
                  {ELLE_YONTEMLER.map((y) => (
                    <option key={y} value={y}>
                      {YONTEM_ADI[y]}
                    </option>
                  ))}
                </Secim>

                <Alan
                  etiket="Not (dekont no, açıklama)"
                  value={not}
                  onChange={(e) => setNot(e.target.value)}
                />

                <div className="flex gap-2">
                  <Buton type="submit" disabled={mut.isPending}>
                    {mut.isPending ? "İşleniyor…" : "Tahsil edildi olarak işaretle"}
                  </Buton>
                  <Buton gorunum="sessiz" type="button" onClick={() => setAcik(null)}>
                    Vazgeç
                  </Buton>
                </div>
              </form>
            ) : null
          }
        />
      )}

      {mut.isError && (
        <p role="alert" className="text-hata-500 text-sm">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Tahsilat işlenemedi."}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// PAKETLER
// ---------------------------------------------------------------

function Paketler() {
  const [formAcik, setFormAcik] = useState(false);
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["paketler"],
    queryFn: paketleriGetir,
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Paketler alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (formAcik) return <YeniPaket onKapat={() => setFormAcik(false)} />;

  if (data.length === 0) {
    return (
      <BosDurum
        baslik="Paket tanımlanmamış"
        aciklama="Abonelik açmak için önce paket gerekir."
        eylem={<Buton onClick={() => setFormAcik(true)}>Paket tanımla</Buton>}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Buton onClick={() => setFormAcik(true)}>Paket tanımla</Buton>
      </div>
      {/*
        HAM `<table>` DEĞİL `Tablo` — dar ekranda YATAY KAYDIRMA yerine
        kart düzeni.

        Buradaki tablo `overflow-x-auto` ile sarılıydı: telefonda beş
        sütun ekrana sığmıyor ve kullanıcı sağa kaydırmak zorunda
        kalıyordu. `Tablo` satırları kart gibi diziyor ve `mobil`
        işaretsiz sütunları gizliyor — CLAUDE.md'nin "mobil öncelikli"
        kuralı personel ekranları için de geçerli.

        Bu tablo bir KAYIT LİSTESİ olduğu için dönüştürülebiliyor.
        Depodaki öteki ham tablolar MATRİS (ısı haritası, huni,
        kapasite) ve onlarda satır kadar SÜTUN da anlam taşıyor; kart
        düzeni matrisi bozardı, o yüzden onlara dokunulmadı.
      */}
      <Tablo
        satirlar={data}
        anahtar={(p) => p.id}
        kartBasligi={0}
        sutunlar={[
          { baslik: "Paket", mobil: true, className: "font-medium", hucre: (p) => p.name },
          { baslik: "Süre", mobil: true, hucre: (p) => PERIYOT_ADI[p.period] },
          { baslik: "Fiyat", mobil: true, sayisal: true, hucre: (p) => para(p.fiyat) },
          {
            baslik: "Görüşme hakkı",
            sayisal: true,
            /*
              `null` = paket görüşme hakkı TANIMLAMIYOR. "0" yazmak
              "hakkı yok" demek olurdu; ikisi ayrı şey.
            */
            hucre: (p) =>
              p.sessionQuota === null ? (
                <span className="text-notr-600">tanımsız</span>
              ) : (
                p.sessionQuota
              ),
          },
          {
            baslik: "Durum",
            mobil: true,
            hucre: (p) => (
              <span
                className={cn(
                  "rounded-alan px-2.5 py-0.5 text-xs font-medium",
                  p.isActive ? "bg-basarili-500/15 text-onay-700" : "bg-notr-100 text-notr-600",
                )}
              >
                {p.isActive ? "Aktif" : "Pasif"}
              </span>
            ),
          },
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------
// ABONELIK KARTI — ogrenci baglaminda gosterilir
// ---------------------------------------------------------------

/**
 * ================================================================
 * PARA DUVARI EKRANDA DA TIPTE
 * ================================================================
 * Sunucu iki ayri sekil donuyor. `mod === "TAM"` daraltmasi olmadan
 * `toplamTutar` okumak DERLEME HATASI — yani uzmanin goremeyecegi bir
 * alani yanlislikla gostermek yapisal olarak mumkun degil.
 *
 * Uzman icin ayri bir bilesen yazmak da bir secenekti ama daha
 * kotuydu: iki bilesen, iki ayri yerde bakim ve birinde alani
 * gizlemeyi unutma ihtimali.
 */
export function AbonelikKarti({
  ogrenciId,
  acabilir = false,
}: {
  ogrenciId: string;
  /**
   * Abonelik açma düğmesi gösterilsin mi.
   *
   * Uç `finans.tahsilat.isle` istiyor; uzmanda o izin yok ve zaten
   * tutarları da göremiyor (yukarıdaki para duvarı). Çağıran ekran
   * izne bakıp veriyor — burada rol adına bakmıyoruz.
   */
  acabilir?: boolean;
}) {
  const [acForm, setAcForm] = useState(false);
  const { data, isPending, isError, error } = useQuery<AbonelikGorunumu | null>({
    queryKey: ["abonelik", ogrenciId],
    queryFn: () => abonelikGetir(ogrenciId),
  });

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <p role="alert" className="text-hata-500 text-sm">
        {error instanceof ApiHatasi ? error.message : "Abonelik bilgisi alınamadı."}
      </p>
    );
  }

  if (acForm) return <AbonelikAcForm ogrenciId={ogrenciId} onKapat={() => setAcForm(false)} />;

  if (!data) {
    return (
      /*
        ================================================================
        CUMLE KENDI DUGMESIYLE CELISIYORDU — 04.09.2026
        ================================================================
        Aciklama "Bu öğrenci için henüz PAKET TANIMLANMAMIŞ" diyordu ama
        buraya girilen kosul `!data`, yani BU OGRENCININ ABONELIGI YOK.
        Paketler sistemde tanimli olabilir — hatta yanindaki "Abonelik aç"
        dugmesi tam da onlari secmek icin duruyor.

        Zarar: kullanici oğrenciye abonelik acmak yerine paket
        tanimlamaya gonderiliyordu. Cumle, yanindaki dugmenin yaptigi
        isi yalanliyordu.

        Paket GERCEKTEN yoksa onu `AbonelikAcForm` soyluyor ("Aktif
        paket yok → Finans > Paketler"). Iki ayri durumu iki ayri yerde
        soylemek dogru; burada onun yerine gecmek yanlisti.
      */
      <BosDurum
        baslik="Aktif abonelik yok"
        aciklama="Bu öğrenci için henüz abonelik açılmamış. Paket seçerek açabilirsiniz."
        eylem={acabilir ? <Buton onClick={() => setAcForm(true)}>Abonelik aç</Buton> : undefined}
      />
    );
  }

  return (
    <Kart className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-notr-900 text-sm font-semibold">{data.paketAdi}</h3>
          <p className="text-notr-600 mt-0.5 text-xs">
            {tarih(data.baslangic)} – {tarih(data.bitis)}
          </p>
        </div>
        <span
          className={cn(
            "rounded-alan shrink-0 px-2.5 py-0.5 text-xs font-medium",
            data.aktifMi ? "bg-basarili-500/15 text-onay-700" : "bg-notr-100 text-notr-600",
          )}
        >
          {data.aktifMi ? "Aktif" : data.durum}
        </span>
      </div>

      <p className="text-notr-700 text-sm">
        Kalan görüşme hakkı:{" "}
        {/*
          `null` = paket gorusme hakki TANIMLAMIYOR. "0" yazmak
          "hakkin bitti" demek olurdu ve uzman bosuna gorusme
          planlamaktan cekinirdi.
        */}
        {data.kalanGorusme === null ? (
          <span className="text-notr-600">tanımlı değil</span>
        ) : (
          <strong>
            {data.kalanGorusme}
            {data.toplamGorusme !== null ? ` / ${data.toplamGorusme}` : ""}
          </strong>
        )}
      </p>

      {data.mod === "OZET" ? (
        /*
          Uzmanin gordugu hal. Neden para gormedigini SOYLEMEK gerekiyor:
          bos bir alan "sistem bozuk" gibi okunur ve uzman finansa
          sorar.
        */
        <p className="rounded-alan bg-notr-50 text-notr-600 p-3 text-xs">
          Ödeme bilgileri bu ekranda gösterilmiyor. Hizmet kalitesinin ödeme durumuna bağlı olmaması
          için bilinçli bir kısıt — soru olursa finans birimine yazın.
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-notr-600">Toplam</dt>
            <dd className="text-right font-medium tabular-nums">{para(data.toplamTutar)}</dd>
            <dt className="text-notr-600">İndirim</dt>
            <dd className="text-right tabular-nums">{para(data.indirim)}</dd>
            <dt className="text-notr-600">Ödenen</dt>
            {/*
              ================================================================
              `text-basarili-500` DEGIL `text-onay-700` — OLCULDU
              ================================================================
              `basarili-500` (oklch 0.65 0.16 150 = #2ea957) beyaz zeminde
              **3,03**. WCAG AA normal metin icin 4,5 istiyor; 3,0 esigi
              METIN OLMAYAN ogeler icin (ikon, cubuk, kenarlik). Yani bu
              ton bir CUBUK rengi olarak gecerli, YAZI rengi olarak degil.

              `onay-700` = 7,34. Renk ayni ailede, anlam degismiyor.
            */}
            <dd className="text-onay-700 text-right tabular-nums">{para(data.odenen)}</dd>
            <dt className="text-notr-700 font-medium">Kalan</dt>
            <dd className="text-right font-semibold tabular-nums">{para(data.kalan)}</dd>
          </dl>

          <TaksitListesi taksitler={data.taksitler} />
        </>
      )}
    </Kart>
  );
}

function TaksitListesi({ taksitler }: { taksitler: AbonelikTaksiti[] }) {
  if (taksitler.length === 0) {
    return <p className="text-notr-600 text-sm">Taksit kaydı yok.</p>;
  }

  return (
    <div className="border-notr-200 overflow-x-auto border-t pt-3">
      <table className="w-full text-sm">
        <caption className="sr-only">Ödeme planı</caption>
        <thead>
          <tr className="text-notr-600 text-left text-xs">
            <th scope="col" className="pb-2 font-medium">
              #
            </th>
            <th scope="col" className="pb-2 text-right font-medium">
              Tutar
            </th>
            <th scope="col" className="pb-2 pl-3 font-medium">
              Vade
            </th>
            <th scope="col" className="pb-2 pl-3 font-medium">
              Durum
            </th>
          </tr>
        </thead>
        <tbody>
          {taksitler.map((t) => (
            <tr key={t.id} className="border-notr-100 border-t">
              <td className="text-notr-600 py-2 tabular-nums">{t.sira}</td>
              <td className="py-2 text-right tabular-nums">{para(t.tutar)}</td>
              <td className="text-notr-600 py-2 pl-3">{tarih(t.vade)}</td>
              <td className="py-2 pl-3">
                {t.odendiMi ? (
                  <span className="text-onay-700">
                    Ödendi{t.yontem ? ` · ${YONTEM_ADI[t.yontem]}` : ""}
                  </span>
                ) : t.gecikmisMi ? (
                  <span className="text-hata-500 font-medium">Gecikmiş</span>
                ) : (
                  <span className="text-notr-600">Bekliyor</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------
// YENI PAKET — F-BIL-01
// ---------------------------------------------------------------

/**
 * `POST /finans/paketler` 06.08.2026'da yazıldı, hiçbir ekran
 * çağırmıyordu: paket listesi görünüyor ama paket TANIMLANAMIYORDU.
 * Sistem boş açıldığı için ilk paket olmadan hiçbir abonelik de
 * açılamıyor — yani bu form olmadan finans modülü hiç başlamıyordu.
 */
function YeniPaket({ onKapat }: { onKapat: () => void }) {
  const istemci = useQueryClient();
  const [ad, setAd] = useState("");
  const [periyot, setPeriyot] = useState<Periyot>("MONTHLY");
  const [fiyat, setFiyat] = useState("");
  const [hakVar, setHakVar] = useState(false);
  const [hak, setHak] = useState("8");

  const mut = useMutation({
    mutationFn: () =>
      paketTanimla({
        ad: ad.trim(),
        periyot,
        fiyat: fiyat.trim(),
        // `null` ile `0` AYRI: null "paket görüşme hakkı tanımlamıyor",
        // 0 ise "hakkı yok" demek. Tabloda da öyle gösteriliyor.
        gorusmeHakki: hakVar ? Number(hak) : null,
      }),
    onSuccess: async () => {
      await istemci.invalidateQueries({ queryKey: ["paketler"] });
      onKapat();
    },
  });

  // Para `Decimal` — metin olarak gidiyor, sunucu doğruluyor. Burada
  // `Number`a çevirmiyoruz: kayan nokta parayı bozar (CLAUDE.md 8).
  const fiyatGecerli = /^\d+([.,]\d{1,2})?$/.test(fiyat.trim());
  const eksik = ad.trim().length < 2 || !fiyatGecerli;

  return (
    <Kart baslik="Yeni paket" aciklama="Paket tanımlanmadan abonelik açılamaz." /* F-BIL-01 */>
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Alan
            etiket="Paket adı"
            value={ad}
            onChange={(e) => setAd(e.target.value)}
            yardim="En az 2 karakter."
          />
          <Secim
            etiket="Süre"
            value={periyot}
            onChange={(e) => setPeriyot(e.target.value as Periyot)}
          >
            {(Object.keys(PERIYOT_ADI) as Periyot[]).map((p) => (
              <option key={p} value={p}>
                {PERIYOT_ADI[p]}
              </option>
            ))}
          </Secim>
          <Alan
            etiket="Fiyat"
            inputMode="decimal"
            value={fiyat}
            onChange={(e) => setFiyat(e.target.value)}
            hata={fiyat.length > 0 && !fiyatGecerli ? "Örnek: 4500 veya 4500.00" : undefined}
          />
        </div>

        <label className="text-notr-700 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={hakVar}
            onChange={(e) => setHakVar(e.target.checked)}
          />
          <span>
            Bu paket koçluk görüşme hakkı tanımlıyor
            <span className="text-notr-600 block text-xs">
              İşaretlenmezse paket görüşme hakkı tanımlamaz; sıfır hak vermekle aynı şey değildir.
            </span>
          </span>
        </label>

        {hakVar && (
          <Alan
            etiket="Görüşme hakkı (adet)"
            type="number"
            min={0}
            max={500}
            value={hak}
            onChange={(e) => setHak(e.target.value)}
          />
        )}

        {mut.isError && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {mut.error instanceof ApiHatasi ? mut.error.message : "Paket tanımlanamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton yukleniyor={mut.isPending} disabled={eksik} onClick={() => mut.mutate()}>
            Paketi tanımla
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

// ---------------------------------------------------------------
// ABONELIK AC — F-BIL-02/03
// ---------------------------------------------------------------

/**
 * `POST /finans/abonelikler` de ölü istemciydi: abonelik kartı
 * "abonelik yok" diyordu ama AÇMANIN yolu yoktu.
 *
 * Ödeme planını sunucu üretiyor — taksit sayısı, ilk vade ve ara ay
 * verilir, taksitler orada hesaplanır. Ekranda hesaplamıyoruz:
 * iki yerde iki hesap, biri değiştiğinde sessizce ayrışırdı.
 */
export function AbonelikAcForm({ ogrenciId, onKapat }: { ogrenciId: string; onKapat: () => void }) {
  const istemci = useQueryClient();
  const bugun = yerelGunBasi().toISOString().slice(0, 10);
  const [paketId, setPaketId] = useState("");
  const [baslangic, setBaslangic] = useState(bugun);
  const [bitis, setBitis] = useState("");
  const [indirim, setIndirim] = useState("");
  const [taksit, setTaksit] = useState("1");
  const [ilkVade, setIlkVade] = useState(bugun);
  const [araAy, setAraAy] = useState("1");

  const paketler = useQuery({ queryKey: ["paketler"], queryFn: paketleriGetir });

  const mut = useMutation({
    mutationFn: () =>
      abonelikAc({
        ogrenciId,
        paketId,
        baslangic,
        bitis,
        indirim: indirim.trim() === "" ? null : indirim.trim(),
        taksitSayisi: Number(taksit),
        ilkVade,
        araAy: Number(araAy),
      }),
    onSuccess: async () => {
      await istemci.invalidateQueries({ queryKey: ["abonelik", ogrenciId] });
      onKapat();
    },
  });

  if (paketler.isPending) return <Iskelet satir={4} />;

  /*
    ================================================================
    ÖLÇÜM DÜŞÜNCE EKRAN YANLIŞ İŞ EMRİ VERİYORDU
    ================================================================
    Yalnızca `isPending` karşılanıyordu; hata hâlinde `paketler.data`
    `undefined` kalıyor, `?? []` onu boş listeye çeviriyor ve ekran
    **"Aktif paket yok — önce bir paket tanımlayın"** diyordu.

    Buradaki zarar bir cümleden büyük: bu bir YÖNLENDİRME. Talimatı
    izleyen finans sorumlusu, zaten var olan paketin **ikinci
    kopyasını** açıyor. Yani ölçüm hatası, veriye yazan bir hataya
    dönüşüyor.

    Aynı veriyi okuyan `Paketler` sekmesi bunu doğru yapıyor
    (`isError` dalı var). Aynı sorunun iki ekranda iki farklı cevabı
    olması, buradaki eksikliğin unutulmuş olduğunun kendisi.
  */
  if (paketler.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Paketler alınamadı"
        aciklama={
          paketler.error instanceof ApiHatasi
            ? paketler.error.message
            : "Paket listesi okunamadığı için abonelik açılamıyor. Bu, tanımlı paket olmadığı anlamına gelmez."
        }
        eylem={
          <Buton gorunum="ikincil" onClick={() => void paketler.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const aktifPaketler = paketler.data.filter((p) => p.isActive);

  if (aktifPaketler.length === 0) {
    return (
      <BosDurum
        baslik="Aktif paket yok"
        aciklama="Abonelik açmadan önce Finans > Paketler ekranından bir paket tanımlayın."
        eylem={
          <Buton gorunum="ikincil" onClick={onKapat}>
            Kapat
          </Buton>
        }
      />
    );
  }

  const tarihSirasi = bitis !== "" && bitis > baslangic;
  const eksik = paketId === "" || bitis === "" || !tarihSirasi;

  return (
    <Kart baslik="Abonelik aç" aciklama="Ödeme planını sunucu üretir." /* F-BIL-03 */>
      <div className="flex flex-col gap-4">
        <Secim etiket="Paket" value={paketId} onChange={(e) => setPaketId(e.target.value)}>
          <option value="">Seçiniz</option>
          {aktifPaketler.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {para(p.fiyat)} / {PERIYOT_ADI[p.period]}
            </option>
          ))}
        </Secim>

        <div className="grid gap-4 sm:grid-cols-2">
          <Alan
            etiket="Başlangıç"
            type="date"
            value={baslangic}
            onChange={(e) => setBaslangic(e.target.value)}
          />
          <Alan
            etiket="Bitiş"
            type="date"
            value={bitis}
            onChange={(e) => setBitis(e.target.value)}
            hata={bitis !== "" && !tarihSirasi ? "Bitiş, başlangıçtan sonra olmalı." : undefined}
          />
          <Alan
            etiket="İndirim"
            inputMode="decimal"
            value={indirim}
            onChange={(e) => setIndirim(e.target.value)}
            yardim="Boş bırakılırsa indirim uygulanmaz."
          />
          <Alan
            etiket="Taksit sayısı"
            type="number"
            min={1}
            max={36}
            value={taksit}
            onChange={(e) => setTaksit(e.target.value)}
          />
          <Alan
            etiket="İlk vade"
            type="date"
            value={ilkVade}
            onChange={(e) => setIlkVade(e.target.value)}
          />
          <Alan
            etiket="Taksit arası (ay)"
            type="number"
            min={1}
            max={12}
            value={araAy}
            onChange={(e) => setAraAy(e.target.value)}
          />
        </div>

        {mut.isError && (
          <p role="alert" className="text-hata-700 text-sm font-medium">
            {mut.error instanceof ApiHatasi ? mut.error.message : "Abonelik açılamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Buton yukleniyor={mut.isPending} disabled={eksik} onClick={() => mut.mutate()}>
            Aboneliği aç
          </Buton>
          <Buton gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </div>
    </Kart>
  );
}
